import { z } from 'zod';
import { CitationSchema, ValidationErrorSchema } from './generation';

/**
 * Project status
 */
export const ProjectStatusSchema = z.enum(['draft', 'generating', 'ready', 'error']);

export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

/**
 * Project schema
 */
export const ProjectSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).nullable(),
  status: ProjectStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Project = z.infer<typeof ProjectSchema>;

/**
 * Create project request
 */
export const CreateProjectRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
});

export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

/**
 * Update project request
 */
export const UpdateProjectRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).nullable().optional(),
});

export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;

/**
 * Generation history item
 */
export const GenerationHistorySchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  prompt: z.string(),
  code: z.string(),
  componentName: z.string(),
  tokensUsed: z.number().int(),
  generationTimeMs: z.number().int(),
  modelVersion: z.string().nullable(),
  isValid: z.boolean(),
  validationErrors: z.array(ValidationErrorSchema),
  citations: z.array(CitationSchema),
  createdAt: z.string().datetime(),
});

export type GenerationHistory = z.infer<typeof GenerationHistorySchema>;

/**
 * Project with generations
 */
export const ProjectWithGenerationsSchema = ProjectSchema.extend({
  generations: z.array(GenerationHistorySchema),
  latestGeneration: GenerationHistorySchema.nullable(),
});

export type ProjectWithGenerations = z.infer<typeof ProjectWithGenerationsSchema>;
